<template>
    <v-container fluid class="pa-0 fill-height" style="background-color: #20242f; position: relative;">

        <div v-if="fetchingPatients" class="loader-overlay">
            <div class="loader-spinner"></div>
        </div>

        <v-row
            no-gutters
            class="fill-height"
            align="center"
            justify="center"
        >
            
            <v-col cols="2">

                <v-card
                    height="100vh"
                    width="100%"
                    style="overflow: auto;"
                >
                    <p class="ma-1 mt-2 text-h5">
                        <v-icon icon="mdi-account" class="mb-1" />
                        Patients
                    </p>
                    <hr>

                    <v-list>
                        <v-list-item
                            v-for="(item, i) in sortBy(patients, ['id'])"
                            :key="i"
                            :class="{ 'bg-blue text-white': item.id === patient?.id }"
                            :value="item"
                            @click="selectPatient(item)"
                        >
                            <v-list-item-title v-text="item.id"></v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-card>

            </v-col>

            <v-col cols="10">

                <v-row
                    no-gutters
                    align="center"
                    justify="center"
                >
                    <v-col cols="11">

                        <v-row
                            no-gutters
                            align="center"
                            justify="center"
                        >

                            <v-col cols="6" class="pr-2" style="height: 25vh">
                                <v-card class="d-flex flex-column" style="height: 100%;">
                                    <div>
                                        <p class="ma-1 mt-2 text-h5">
                                            <v-icon icon="mdi-map-marker-account" class="mb-1" />
                                            Lesions
                                        </p>
                                        <hr>
                                    </div>

                                    <div class="flex-grow-1" style="overflow-y: auto;">
                                        <v-list>
                                            <v-list-item
                                                v-for="(item, i) in sortBy(patient?.lesions, ['id'])"
                                                :key="i"
                                                :class="{ 'bg-blue text-white': item.id === lesion?.id }"
                                                @click="selectLesion(item)"
                                            >
                                                <v-list-item-title v-text="item.id"></v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </div>
                                </v-card>
                            </v-col>

                            <v-col cols="6" class="pl-2" style="height: 25vh;">
                                <v-card class="d-flex flex-column" style="height: 100%;">
                                    <div>
                                        <p class="ma-1 mt-2 text-h5">
                                            <v-icon icon="mdi-folder-multiple-image" class="mb-1" />
                                            Exams
                                        </p>
                                        <hr>
                                    </div>
        
                                    <div class="flex-grow-1" style="overflow-y: auto;">
                                        <v-list>
                                            <v-list-item
                                                v-for="(item, i) in sortBy(lesion?.exams, ['id'])"
                                                :key="i"
                                                :class="{ 'bg-blue text-white': item.id === exam?.id }"
                                                :value="item"
                                                @click="exam = item"
                                            >
                                                <v-list-item-title v-text="item.id"></v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </div>
                                </v-card>
                            </v-col>

                            <v-col cols="12" class="mt-4" style="height: 65vh;">
                                <v-card height="100%" class="d-flex flex-column">
                                    <div>
                                        <p class="ma-1 mt-2 text-h5">
                                            <v-icon icon="mdi-image" class="mb-1" />
                                            Acquisitions
                                        </p>
                                        <hr>
                                    </div>

                                    <div class="flex-grow-1" style="overflow-y: auto;">
                                        <v-row v-if="exam && exam.acquisitions.length" no-gutters class="my-1">
                                            <v-col
                                                v-for="(item, i) in sortBy(exam.acquisitions, ['id'])"
                                                :key="i"
                                                cols="12"
                                                sm="6"
                                                md="4"
                                                lg="3"
                                                class="text-center"
                                            >
                                                <v-hover
                                                    v-if="item.medias.thumbnailURL && item.medias.fullResolutionURL"
                                                    v-slot="{ isHovering, props }"
                                                >
                                                    <v-card
                                                        v-bind="props"
                                                        :elevation="isHovering ? 8 : 2"
                                                        class="cursor-pointer my-2"
                                                        style="width: 100%; height: 260px;"
                                                        @click="selectImage(item.medias.fullResolutionURL);"
                                                    >
                                                        <img
                                                            :src="item.medias.thumbnailURL"
                                                            style="max-width: 95%; max-height: 90%; object-fit: contain;"
                                                        />
                                                        <p style="position: absolute; bottom: 0px; left: 10px; font-size: 20px;">
                                                            <b>IM 00{{ item.id }}</b> - {{ item.details.date }} {{ formatTime(item.details.time) }}
                                                        </p>
                                                    </v-card>
                                                </v-hover>

                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>

                    </v-col>
                </v-row>

            </v-col>

            <ImageZoomOverlay
                v-model="imageSelected"
                :images="[{
                    src: imageURLSelected,
                    title: 'Image'
                }]"
            />

            <v-snackbar
                v-model="showSnackbar"
                :color="snackbarColor"
                timeout="3000"
            >
                {{ snackbarMessage }}
            </v-snackbar>

        </v-row>
    </v-container>
</template>

<style lang="css" scoped>
.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(32, 36, 47, 0.6); /* semi-transparent shadow */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loader-spinner {
  border: 6px solid #f3f3f3; /* light gray */
  border-top: 6px solid #1976d2; /* primary color */
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

<script lang="ts" src="./Home.ts" />